import Image from 'next/image'
import React, { memo, useCallback } from 'react'
import { match } from 'ts-pattern'

import { cn, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  vendor: 'CARD' | 'KAKAO' | 'NAVER' | 'FREE'
  name: string
  cardNumber: string | null
  actionTrigger?: React.ReactNode
  className?: string
}

const RegisteredPaymentMethod = memo(({ vendor, name, cardNumber, actionTrigger, className }: Props) => {
  const VendorImage = () => {
    return match(vendor)
      .with('CARD', () => <Image src="/images/credit-card.png" alt="credit-card" width={100} height={100} />)
      .with('KAKAO', () => <Image src="/images/kakao-pay-yellow-small.png" alt="kakao-pay" width={50} height={20} />)
      .with('NAVER', () => <Image src="/images/naver-pay.png" alt="naver-pay" width={100} height={100} />)
      .otherwise(() => null)
  }

  const formatCardNumber = useCallback((cardNumber: string | null) => {
    if (!cardNumber) {
      return ''
    }
    // 백엔드가 카드번호 앞 4자리(BIN)만 내려주므로, 앞 4자리 + 나머지 마스킹으로 표시한다.
    const digits = cardNumber.replace(/[^0-9]/g, '').slice(0, 4)
    if (!digits) {
      return ''
    }
    return `${digits} **** **** ****`
  }, [])

  const maskingEmail = (email: string | null) => {
    if (!email) {
      return ''
    }
    const parts = email.split('@')
    if (parts.length !== 2) {
      return ''
    }
    const [local, domain] = parts
    const maskedLocal = local.slice(0, 2) + '*'.repeat(Math.max(0, local.length - 2))
    return `${maskedLocal}@${domain}`
  }

  return (
    <HStack className={cn('gap-4 border border-gray-200 rounded-lg p-5 items-center', className)}>
      <VendorImage />

      <VStack className="gap-0.5 flex-1">
        <Typography size="h3">{name}</Typography>
        {match(vendor)
          .with('CARD', () => (
            <Typography size="body1" className="text-gray-400">
              {formatCardNumber(cardNumber)}
            </Typography>
          ))
          .with('KAKAO', () => (
            <Typography size="body1" className="text-gray-400">
              {maskingEmail(cardNumber)}
            </Typography>
          ))
          .otherwise(() => null)}
      </VStack>

      {actionTrigger}
    </HStack>
  )
})

export default RegisteredPaymentMethod
